/**
 * AccountsSection -- 계좌번호 섹션 편집 (본인 + 부모님)
 */

import { Wallet } from 'lucide-react'
import { useRef, useState } from 'react'
import { useInvitationStore } from '../../../stores/invitationStore.js'
import { Input, Select } from '../../ui/index.js'
import styles from './SectionForm.module.css'

const BANKS = [
  '국민은행', '신한은행', '우리은행', '하나은행', 'SC제일은행',
  '기업은행', '농협은행', '수협은행', '카카오뱅크', '토스뱅크',
  '케이뱅크', '새마을금고', '우체국', '신협', '광주은행',
  '경남은행', '대구은행', '부산은행', '전북은행', '제주은행',
] as const

type PersonType = 'groom' | 'bride'

interface AccountCardProps {
  type: PersonType
  person: Record<string, unknown> | undefined
  updateNested: (path: string, value: unknown) => void
}

const BANK_OPTIONS = [
  { value: '', label: '은행 선택' },
  ...BANKS.map((bank) => ({ value: bank, label: bank })),
]

function BankSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Select
      label="은행"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      options={BANK_OPTIONS}
    />
  )
}

function AccountNumberInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [error, setError] = useState<string | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  function showError(msg: string) {
    setError(msg)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setError(null), 1500)
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value
    let cleaned = raw.replace(/[^\d-]/g, '')
    if (raw !== cleaned) {
      showError('숫자와 하이픈(-)만 입력 가능합니다')
    }
    // 연속 하이픈 → 단일 하이픈으로 정규화
    cleaned = cleaned.replace(/-{2,}/g, '-')
    // 맨 앞 하이픈 제거
    cleaned = cleaned.replace(/^-/, '')
    onChange(cleaned)
  }

  return (
    <Input
      label="계좌번호"
      value={value}
      inputMode="numeric"
      placeholder="000-0000-0000-00"
      onChange={handleChange}
      error={error ?? undefined}
    />
  )
}

function AccountCard({ type, person, updateNested }: AccountCardProps) {
  const label = type === 'groom' ? '신랑' : '신부'
  const account = person?.account as Record<string, string> | undefined
  const parentAccounts = person?.parentAccounts as
    | { father?: Array<Record<string, string>>; mother?: Array<Record<string, string>> }
    | undefined

  const fatherAccount = parentAccounts?.father?.[0]
  const motherAccount = parentAccounts?.mother?.[0]

  function updateParentAccount(role: 'father' | 'mother', field: string, value: string) {
    const current = (role === 'father' ? fatherAccount : motherAccount) ?? {
      bank: '',
      accountNumber: '',
      accountHolder: '',
    }
    const updated = { ...current, [field]: value }
    updateNested(`${type}.parentAccounts.${role}`, [updated])
  }

  return (
    <div className={styles.personCard} data-person={type}>
      <div className={styles.personCardHeader}>
        <span className={styles.personCardIconBadge}>
          <Wallet size={20} />
        </span>
        <span className={styles.personCardLabel}>{label} 측</span>
      </div>
      <div className={styles.personCardDivider} />
      <div className={styles.personCardBody}>
        <BankSelect
          value={account?.bank ?? ''}
          onChange={(v) => updateNested(`${type}.account.bank`, v)}
        />
        <AccountNumberInput
          value={account?.accountNumber ?? ''}
          onChange={(v) => updateNested(`${type}.account.accountNumber`, v)}
        />
        <Input
          label="예금주"
          value={account?.accountHolder ?? ''}
          onChange={(e) => updateNested(`${type}.account.accountHolder`, e.target.value)}
        />

        <div className={styles.subSection}>
          <span className={styles.subSectionTitle}>부모님 계좌</span>

          <div className={styles.row}>
            <div className={styles.fieldGroup}>
              <span className={styles.subSectionLabel}>아버지</span>
              <BankSelect
                value={fatherAccount?.bank ?? ''}
                onChange={(v) => updateParentAccount('father', 'bank', v)}
              />
              <AccountNumberInput
                value={fatherAccount?.accountNumber ?? ''}
                onChange={(v) => updateParentAccount('father', 'accountNumber', v)}
              />
              <Input
                label="예금주"
                value={fatherAccount?.accountHolder ?? ''}
                onChange={(e) => updateParentAccount('father', 'accountHolder', e.target.value)}
              />
            </div>
            <div className={styles.fieldGroup}>
              <span className={styles.subSectionLabel}>어머니</span>
              <BankSelect
                value={motherAccount?.bank ?? ''}
                onChange={(v) => updateParentAccount('mother', 'bank', v)}
              />
              <AccountNumberInput
                value={motherAccount?.accountNumber ?? ''}
                onChange={(v) => updateParentAccount('mother', 'accountNumber', v)}
              />
              <Input
                label="예금주"
                value={motherAccount?.accountHolder ?? ''}
                onChange={(e) => updateParentAccount('mother', 'accountHolder', e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function AccountsSection() {
  const invitation = useInvitationStore((s) => s.invitation)
  const updateNested = useInvitationStore((s) => s.updateNested)
  if (!invitation) return null

  return (
    <div className={styles.personCardRow}>
      <AccountCard
        type="groom"
        person={invitation.groom as Record<string, unknown> | undefined}
        updateNested={updateNested}
      />
      <AccountCard
        type="bride"
        person={invitation.bride as Record<string, unknown> | undefined}
        updateNested={updateNested}
      />
    </div>
  )
}
